import React, { useState, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronDown, X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useTranslation } from 'react-i18next';

/**
 * Get a date range relative to today
 */
function getDateRange(daysBack, daysForward) {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - daysBack);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setDate(endDate.getDate() - daysForward);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Get this week (Sunday to today)
 */
function getThisWeek() {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(now.getDate() - now.getDay());
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Get last week (Sunday to Saturday)
 */
function getLastWeek() {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(now.getDate() - now.getDay() - 7);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(now);
  endDate.setDate(now.getDate() - now.getDay() - 1);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Get this month (1st to today)
 */
function getThisMonth() {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Get last month (1st to last day)
 */
function getLastMonth() {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Get last 3 calendar months (today minus 3 months → today). Differs from
 * "Last 90 Days" by a few days because months aren't exactly 30 days.
 */
function getLast3Months() {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setMonth(now.getMonth() - 3);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Format a date for display
 */
function formatDateDisplay(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Format date for input[type="date"]
 */
function formatDateForInput(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

const DateRangeSelector = ({
  startDate,
  endDate,
  onChange,
  className = ''
}) => {
  const { t } = useTranslation();

  const DATE_PRESETS = useMemo(() => [
    { id: 'today', label: t('shared.today', 'Today'), getValue: () => getDateRange(0, 0) },
    { id: 'yesterday', label: t('shared.yesterday', 'Yesterday'), getValue: () => getDateRange(1, 1) },
    { id: 'this_week', label: t('shared.this_week', 'This Week'), getValue: () => getThisWeek() },
    { id: 'last_week', label: t('shared.last_week', 'Last Week'), getValue: () => getLastWeek() },
    { id: 'this_month', label: t('shared.this_month', 'This Month'), getValue: () => getThisMonth() },
    { id: 'last_month', label: t('shared.last_month', 'Last Month'), getValue: () => getLastMonth() },
    { id: 'last_30_days', label: t('shared.last_30_days', 'Last 30 Days'), getValue: () => getDateRange(30, 0) },
    { id: 'last_3_months', label: t('shared.last_3_months', 'Last 3 Months'), getValue: () => getLast3Months() },
    { id: 'last_90_days', label: t('shared.last_90_days', 'Last 90 Days'), getValue: () => getDateRange(90, 0) },
    { id: 'custom', label: t('shared.custom', 'Custom'), getValue: () => null },
  ], [t]);

  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customStart, setCustomStart] = useState(formatDateForInput(startDate));
  const [customEnd, setCustomEnd] = useState(formatDateForInput(endDate));
  const [validationError, setValidationError] = useState('');

  // Determine current selection label
  const currentLabel = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if matches any preset
    for (const preset of DATE_PRESETS) {
      if (preset.id === 'custom') continue;
      const range = preset.getValue();
      if (range) {
        const presetStart = new Date(range.startDate);
        const presetEnd = new Date(range.endDate);

        // Compare dates (ignoring time for simplicity)
        if (
          presetStart.toDateString() === start.toDateString() &&
          presetEnd.toDateString() === end.toDateString()
        ) {
          return preset.label;
        }
      }
    }

    return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
  }, [startDate, endDate]);

  const handlePresetSelect = (preset) => {
    if (preset.id === 'custom') {
      handleOpenCustomModal();
    } else {
      const range = preset.getValue();
      if (range) {
        onChange(range.startDate.toISOString(), range.endDate.toISOString());
      }
      setShowDropdown(false);
    }
  };

  const handleCustomApply = () => {
    setValidationError('');

    if (!customStart || !customEnd) {
      setValidationError(t('shared.please_select_both_start_and_end_dates', 'Please select both start and end dates'));
      return;
    }

    const start = new Date(customStart);
    const end = new Date(customEnd);

    if (start > end) {
      setValidationError(t('reporting.date_range.error_start_after_end', 'Start date cannot be after end date'));
      return;
    }

    // Check max date range (365 days to match backend limit)
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      setValidationError(t('reporting.date_range.error_max_days', 'Date range cannot exceed 365 days'));
      return;
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    onChange(start.toISOString(), end.toISOString());
    setShowCustomModal(false);
  };

  // Reset validation error when modal opens
  const handleOpenCustomModal = () => {
    setValidationError('');
    setCustomStart(formatDateForInput(startDate));
    setCustomEnd(formatDateForInput(endDate));
    setShowCustomModal(true);
    setShowDropdown(false);
  };

  return (
    <div className={`position-relative ${className}`}>
      {/* Trigger Button */}
      <motion.button
        ref={buttonRef}
        className="btn btn-light d-flex align-items-center gap-2 px-3 py-2"
        onClick={() => {
          if (!showDropdown && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
          }
          setShowDropdown(!showDropdown);
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          borderRadius: '10px',
          border: '1px solid #e0e0e0',
          minWidth: '200px'
        }}
      >
        <Calendar size={18} className="text-primary" />
        <span className="fw-medium">{currentLabel}</span>
        <ChevronDown size={16} className="ms-auto text-muted" />
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100"
              style={{ zIndex: 999 }}
              onClick={() => setShowDropdown(false)}
            />

            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white shadow-lg rounded-3 py-2"
              style={{
                position: 'fixed',
                top: dropdownPos.top,
                left: dropdownPos.left,
                minWidth: Math.max(dropdownPos.width, 200),
                zIndex: 1000
              }}
            >
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  className="dropdown-item px-3 py-2 d-flex align-items-center"
                  onClick={() => handlePresetSelect(preset)}
                  style={{
                    backgroundColor: currentLabel === preset.label ? '#f0f7ff' : 'transparent'
                  }}
                >
                  <span>{preset.label}</span>
                  {currentLabel === preset.label && (
                    <span className="ms-auto text-primary">&#10003;</span>
                  )}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Custom Date Modal */}
      <AnimatePresence>
        {showCustomModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2000,
              backdropFilter: 'blur(5px)'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCustomModal(false);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-4 shadow-lg p-4"
              style={{ minWidth: '400px', maxWidth: '90vw' }}
            >
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="mb-0 fw-bold">{t('shared.select_date_range', 'Select Date Range')}</h5>
                <motion.button
                  className="btn btn-light btn-sm rounded-circle p-2"
                  onClick={() => setShowCustomModal(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X size={16} />
                </motion.button>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('shared.start_date', 'Start Date')}</label>
                  <DatePicker
                    selected={customStart ? new Date(customStart) : null}
                    onChange={(date) => {
                      setCustomStart(date ? date.toISOString().split('T')[0] : '');
                      setValidationError('');
                    }}
                    dateFormat="MMMM d, yyyy"
                    className="form-control"
                    placeholderText={t('shared.select_start_date', 'Select start date')}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t('shared.end_date', 'End Date')}</label>
                  <DatePicker
                    selected={customEnd ? new Date(customEnd) : null}
                    onChange={(date) => {
                      setCustomEnd(date ? date.toISOString().split('T')[0] : '');
                      setValidationError('');
                    }}
                    dateFormat="MMMM d, yyyy"
                    className="form-control"
                    placeholderText={t('shared.select_end_date', 'Select end date')}
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className="text-danger small mt-2">
                  {validationError}
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-4">
                <motion.button
                  className="btn btn-light"
                  onClick={() => setShowCustomModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('shared.cancel', 'Cancel')}
                </motion.button>
                <motion.button
                  className="btn btn-primary"
                  onClick={handleCustomApply}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('shared.apply', 'Apply')}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DateRangeSelector;
